// src/components/Dashboard/SessionsTab.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { app } from '../../firebase/firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Play,
  Users,
  ChevronDown,
  ChevronRight,
  Snowflake,
  Clock,
  RefreshCw,
  Eye
} from 'lucide-react';
import IglooSessionTimeline from './IglooSessionTimeline';

// Format relative time
function formatRelativeTime(timestampMs) {
  if (!timestampMs) return 'Never';
  const now = Date.now();
  const diffMs = now - timestampMs;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Format duration in seconds to human readable
function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '-';
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (seconds < 60) return `${seconds}s`;
  if (minutes < 60) return `${minutes}m`;
  return `${hours}h ${minutes % 60}m`;
}

// Format exact date/time compactly
function formatDateTime(timestampMs) {
  if (!timestampMs) return '-';
  const d = new Date(timestampMs);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Get ms from a Firestore timestamp or ms value
function toMs(ts) {
  if (!ts) return 0;
  if (ts.toMillis) return ts.toMillis();
  if (typeof ts === 'number') return ts;
  return 0;
}

// Calculate duration from start to end (or now if ongoing)
function calculateDuration(startTs, endTs) {
  const startMs = toMs(startTs);
  const endMs = endTs ? toMs(endTs) : Date.now();
  return Math.floor((endMs - startMs) / 1000);
}

// Calculate duration (in seconds) where 2+ families were present simultaneously
function calculateMultiParticipantDuration(participants, isLive) {
  const events = [];
  const now = Date.now();

  for (const p of (participants || [])) {
    for (const interval of (p.intervals || [])) {
      const joinMs = toMs(interval.joinedAt);
      if (!joinMs) continue;
      const leaveMs = interval.leftAt ? toMs(interval.leftAt) : (isLive ? now : 0);
      if (!leaveMs) continue;
      events.push({ time: joinMs, delta: 1 });
      events.push({ time: leaveMs, delta: -1 });
    }
  }

  if (events.length === 0) return 0;

  // Sort by time; ties: joins before leaves so we count the peak correctly
  events.sort((a, b) => a.time - b.time || b.delta - a.delta);

  let concurrent = 0;
  let multiStart = null;
  let totalMs = 0;

  for (const ev of events) {
    concurrent += ev.delta;
    if (concurrent >= 2 && multiStart === null) {
      multiStart = ev.time;
    } else if (concurrent < 2 && multiStart !== null) {
      totalMs += ev.time - multiStart;
      multiStart = null;
    }
  }
  // If still >=2 at end (live session), close off
  if (multiStart !== null) {
    totalMs += now - multiStart;
  }

  return Math.floor(totalMs / 1000);
}

// Get join/leave counts from a session
function getActivityCounts(session) {
  const events = session.events || [];
  const joins = events.filter(e => e.type === 'joined').length;
  const leaves = events.filter(e => e.type === 'left').length;
  return { joins, leaves };
}

// Period options
const PERIODS = [
  { value: '24h', label: '24h', ms: 24 * 60 * 60 * 1000 },
  { value: '7d', label: '7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { value: '14d', label: '14 days', ms: 14 * 24 * 60 * 60 * 1000 },
  { value: '30d', label: '30 days', ms: 30 * 24 * 60 * 60 * 1000 },
];

const SECONDS_24H = 24 * 60 * 60;
const SECONDS_10MIN = 10 * 60;

function SessionsTab() {
  const [accounts, setAccounts] = useState([]);
  const [familyIdToAccount, setFamilyIdToAccount] = useState({});
  const [liveSessions, setLiveSessions] = useState([]);
  const [historySessions, setHistorySessions] = useState([]);
  const [familyNames, setFamilyNames] = useState({});
  const [iglooNames, setIglooNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [timePeriod, setTimePeriod] = useState('7d');
  const [sortMode, setSortMode] = useState('recentSession');
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [selectedSession, setSelectedSession] = useState(null);
  const [hideLongSessions, setHideLongSessions] = useState(false);
  const [hideSoloSessions, setHideSoloSessions] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const db = useMemo(() => getFirestore(app), []);
  const familyNamesRef = useRef(familyNames);
  familyNamesRef.current = familyNames;
  const iglooNamesRef = useRef(iglooNames);
  iglooNamesRef.current = iglooNames;

  // Fetch accounts on mount and build familyId → account mapping
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
        const accountsList = [];
        const familyMap = {};

        querySnapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          const account = { id: docSnap.id, ...data };
          accountsList.push(account);

          (data.families || []).forEach(familyId => {
            familyMap[familyId] = { accountId: docSnap.id, email: data.email, name: data.name };
          });
        });

        accountsList.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
        setAccounts(accountsList);
        setFamilyIdToAccount(familyMap);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };

    fetchAccounts();
  }, [db]);

  // Real-time listener for live sessions
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'iglooSessionsLive'), (snapshot) => {
      const sessions = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        _isLive: true
      }));
      setLiveSessions(sessions);

      const allFamilyIds = new Set();
      const allIglooIds = new Set();
      sessions.forEach(s => {
        (s.participantFamilyIds || s.participants?.map(p => p.familyId) || []).forEach(fid => allFamilyIds.add(fid));
        if (s.iglooId) allIglooIds.add(s.iglooId);
      });
      resolveNames(allFamilyIds, allIglooIds);
    }, (error) => {
      console.error('Error listening to live sessions:', error);
    });

    return () => unsubscribe();
  }, [db]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch history sessions when period changes or refresh is triggered
  useEffect(() => {
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const periodConfig = PERIODS.find(p => p.value === timePeriod);
        const cutoffMs = Date.now() - periodConfig.ms;
        const cutoffTimestamp = Timestamp.fromMillis(cutoffMs);

        const historyQuery = query(
          collection(db, 'iglooSessionsHistory'),
          where('startedAt', '>=', cutoffTimestamp)
        );

        const snapshot = await getDocs(historyQuery);
        const sessions = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
          _isLive: false
        }));

        sessions.sort((a, b) => toMs(b.startedAt) - toMs(a.startedAt));
        setHistorySessions(sessions);

        const allFamilyIds = new Set();
        const allIglooIds = new Set();
        sessions.forEach(s => {
          (s.participantFamilyIds || []).forEach(fid => allFamilyIds.add(fid));
          if (s.iglooId) allIglooIds.add(s.iglooId);
        });
        await resolveNames(allFamilyIds, allIglooIds);
      } catch (error) {
        console.error('Error fetching history sessions:', error);
      } finally {
        setHistoryLoading(false);
        setLoading(false);
      }
    };

    fetchHistory();
  }, [db, timePeriod, refreshCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve family and igloo names (only fetch ones we don't already have)
  const resolveNames = useCallback(async (familyIds, iglooIds) => {
    const newFamilyIds = Array.from(familyIds).filter(fid => !familyNamesRef.current[fid]);
    if (newFamilyIds.length > 0) {
      const names = {};
      await Promise.all(newFamilyIds.map(async (familyId) => {
        try {
          const familyDoc = await getDoc(doc(db, 'familyInfo', familyId));
          names[familyId] = familyDoc.exists() ? (familyDoc.data().name || familyId) : familyId;
        } catch {
          names[familyId] = familyId;
        }
      }));
      setFamilyNames(prev => ({ ...prev, ...names }));
    }

    const newIglooIds = Array.from(iglooIds).filter(iid => !iglooNamesRef.current[iid]);
    if (newIglooIds.length > 0) {
      const names = {};
      await Promise.all(newIglooIds.map(async (iglooId) => {
        try {
          const iglooInfoDoc = await getDoc(doc(db, 'iglooInfo', iglooId));
          if (iglooInfoDoc.exists()) {
            const controllers = iglooInfoDoc.data().controllers || [];
            if (controllers.length > 0) {
              const iglooNameDoc = await getDoc(doc(db, 'familyInfo', controllers[0], 'igloos', iglooId));
              names[iglooId] = iglooNameDoc.exists() ? (iglooNameDoc.data().name || iglooId) : iglooId;
            } else {
              names[iglooId] = iglooId;
            }
          } else {
            names[iglooId] = iglooId;
          }
        } catch {
          names[iglooId] = iglooId;
        }
      }));
      setIglooNames(prev => ({ ...prev, ...names }));
    }
  }, [db]);

  // Filter a session based on toggles
  const sessionPassesFilters = useCallback((session) => {
    if (hideLongSessions) {
      const dur = session._isLive
        ? calculateDuration(session.startedAt, null)
        : (session.durationSeconds || 0);
      if (dur > SECONDS_24H) return false;
    }
    if (hideSoloSessions) {
      const peak = session.peakConcurrent || session.totalParticipants || session.participants?.length || 0;
      if (peak <= 1) return false;
    }
    return true;
  }, [hideLongSessions, hideSoloSessions]);

  // Group sessions by account (only accounts with sessions, after filters)
  const accountsWithSessions = useMemo(() => {
    const sessionsByAccount = {};

    const processSession = (session) => {
      if (!sessionPassesFilters(session)) return;

      const familyIds = session.participantFamilyIds || session.participants?.map(p => p.familyId) || [];
      const involvedAccountIds = new Set();

      familyIds.forEach(fid => {
        const accountInfo = familyIdToAccount[fid];
        if (accountInfo) involvedAccountIds.add(accountInfo.accountId);
      });

      involvedAccountIds.forEach(accountId => {
        if (!sessionsByAccount[accountId]) {
          const account = accounts.find(a => a.id === accountId);
          sessionsByAccount[accountId] = {
            account,
            sessions: [],
            latestMs: 0,
            totalSeconds: 0,
            liveCount: 0
          };
        }

        const group = sessionsByAccount[accountId];
        group.sessions.push(session);

        const startMs = toMs(session.startedAt);
        if (startMs > group.latestMs) group.latestMs = startMs;

        if (session._isLive) {
          group.liveCount++;
          group.totalSeconds += calculateDuration(session.startedAt, null);
        } else {
          group.totalSeconds += (session.durationSeconds || 0);
        }
      });
    };

    liveSessions.forEach(processSession);
    historySessions.forEach(processSession);

    // Sort sessions within each group: live first, then by startedAt desc
    Object.values(sessionsByAccount).forEach(group => {
      group.sessions.sort((a, b) => {
        if (a._isLive && !b._isLive) return -1;
        if (!a._isLive && b._isLive) return 1;
        return toMs(b.startedAt) - toMs(a.startedAt);
      });
    });

    const withSessions = Object.values(sessionsByAccount).filter(g => g.account);

    withSessions.sort((a, b) => {
      switch (sortMode) {
        case 'mostSessions':
          if (b.sessions.length !== a.sessions.length) return b.sessions.length - a.sessions.length;
          return b.latestMs - a.latestMs;
        case 'alphabetic':
          return (a.account.email || '').localeCompare(b.account.email || '');
        case 'recentSession':
        default:
          if (a.liveCount > 0 && b.liveCount === 0) return -1;
          if (b.liveCount > 0 && a.liveCount === 0) return 1;
          return b.latestMs - a.latestMs;
      }
    });

    return withSessions;
  }, [accounts, familyIdToAccount, liveSessions, historySessions, sortMode, sessionPassesFilters]);

  // Toggle account expansion
  const toggleAccount = useCallback((accountId) => {
    setExpandedAccounts(prev => ({ ...prev, [accountId]: !prev[accountId] }));
  }, []);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    setRefreshCounter(c => c + 1);
  }, []);

  // Render a single session row (compact, one-line)
  // sincePrevSeconds: gap in seconds between start of this session and end of previous, or null
  const renderSessionRow = (session, sincePrevSeconds) => {
    const isLive = session._isLive;
    const iglooName = iglooNames[session.iglooId] || session.iglooId;
    const participants = session.participants || [];
    const durationSeconds = isLive
      ? calculateDuration(session.startedAt, null)
      : (session.durationSeconds || calculateDuration(session.startedAt, session.endedAt));
    const startMs = toMs(session.startedAt);
    const endMs = isLive ? null : toMs(session.endedAt);
    const { joins, leaves } = getActivityCounts(session);
    const familyCount = session.totalParticipants || participants.length;
    const multiDuration = calculateMultiParticipantDuration(participants, isLive);

    return (
      <div
        key={session.id + (isLive ? '-live' : '')}
        className={`flex items-center py-1.5 px-3 border-b last:border-b-0 hover:bg-muted/30 text-xs ${isLive ? 'bg-green-50/50' : ''}`}
      >
        {/* Igloo name */}
        <div className="flex items-center gap-1.5 w-[120px] flex-shrink-0">
          {isLive && (
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
          )}
          <Snowflake className={`w-3 h-3 ${isLive ? 'text-green-600' : 'text-cyan-500'} flex-shrink-0`} />
          <span className={`font-medium truncate ${isLive ? 'text-green-700' : ''}`} title={iglooName}>
            {iglooName}
          </span>
        </div>

        {/* Families with durations */}
        <div className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
          {participants.map(p => {
            const name = familyNames[p.familyId] || p.familyId;
            const secs = isLive
              ? calculateDuration(p.intervals?.[0]?.joinedAt, p.intervals?.[p.intervals.length - 1]?.leftAt)
              : (p.totalSeconds || 0);
            const isCurrentlyIn = isLive && p.intervals?.[p.intervals.length - 1] && !p.intervals[p.intervals.length - 1].leftAt;

            return (
              <span
                key={p.familyId}
                className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full flex-shrink-0 ${
                  isCurrentlyIn ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}
                title={`${name}: ${formatDuration(secs)}`}
              >
                {isCurrentlyIn && <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />}
                {name} ({formatDuration(secs)})
              </span>
            );
          })}
        </div>

        {/* Families count */}
        <div className="w-[30px] flex-shrink-0 text-center">
          {familyCount}
        </div>

        {/* Peak concurrent */}
        <div className="w-[30px] flex-shrink-0 text-center text-muted-foreground">
          {session.peakConcurrent || 0}
        </div>

        {/* Joins / Leaves */}
        <div className="w-[45px] flex-shrink-0 text-center text-muted-foreground" title={`${joins} joins, ${leaves} leaves`}>
          {joins}j/{leaves}l
        </div>

        {/* Start */}
        <div className="w-[95px] flex-shrink-0 text-muted-foreground truncate" title={startMs ? new Date(startMs).toLocaleString() : ''}>
          {formatDateTime(startMs)}
        </div>

        {/* End */}
        <div className={`w-[95px] flex-shrink-0 truncate ${isLive ? 'text-green-600' : 'text-muted-foreground'}`} title={endMs ? new Date(endMs).toLocaleString() : ''}>
          {isLive ? 'ongoing' : formatDateTime(endMs)}
        </div>

        {/* Duration */}
        <div className={`w-[55px] flex-shrink-0 ${isLive ? 'text-green-600' : ''}`}>
          {formatDuration(durationSeconds)}
        </div>

        {/* Duration with 2+ families */}
        <div className={`w-[55px] flex-shrink-0 ${multiDuration > 0 ? 'text-blue-600' : 'text-muted-foreground'}`} title="Duration with 2+ families present">
          {multiDuration > 0 ? formatDuration(multiDuration) : '-'}
        </div>

        {/* When (relative) */}
        <div className="w-[55px] flex-shrink-0 text-muted-foreground truncate" title={startMs ? new Date(startMs).toLocaleString() : ''}>
          {formatRelativeTime(startMs)}
        </div>

        {/* Since previous session */}
        <div className={`w-[55px] flex-shrink-0 truncate ${
          sincePrevSeconds !== null && sincePrevSeconds < SECONDS_10MIN ? 'text-red-600 font-medium' : 'text-muted-foreground'
        }`} title={sincePrevSeconds !== null ? `${formatDuration(sincePrevSeconds)} since previous session` : ''}>
          {sincePrevSeconds !== null ? formatDuration(sincePrevSeconds) : '-'}
        </div>

        {/* View button */}
        <div className="w-[30px] flex-shrink-0 flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedSession({
                iglooId: session.iglooId,
                entryTimestamp: startMs
              });
            }}
            className="h-6 w-6 p-0"
            title="View session detail"
          >
            <Eye className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  };

  // Render account section
  const renderAccountSection = (group) => {
    const { account, sessions, totalSeconds, liveCount } = group;
    const isExpanded = expandedAccounts[account.id] !== false; // default expanded
    const latestSession = sessions[0];
    const latestMs = toMs(latestSession?.startedAt);

    // Compute "since" gaps: sessions are sorted most-recent first (desc by startedAt).
    // For session[i], the chronologically previous session is session[i+1].
    // Gap = start of session[i] - end of session[i+1].
    const sinceGaps = sessions.map((session, i) => {
      if (i >= sessions.length - 1) return null; // oldest session, no previous
      const prevSession = sessions[i + 1];
      const prevEndMs = prevSession._isLive ? null : toMs(prevSession.endedAt);
      if (!prevEndMs) return null;
      const thisStartMs = toMs(session.startedAt);
      const gapSeconds = Math.floor((thisStartMs - prevEndMs) / 1000);
      return gapSeconds >= 0 ? gapSeconds : null;
    });

    return (
      <div key={account.id} className="border rounded-lg bg-card overflow-hidden">
        {/* Account header */}
        <div
          className="flex items-center py-2 px-3 cursor-pointer hover:bg-muted/50 text-xs"
          onClick={() => toggleAccount(account.id)}
        >
          <div className="flex items-center gap-2 w-[200px] flex-shrink-0">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
            )}
            <Users className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <span className="font-medium truncate" title={account.email}>
              {account.email}
            </span>
            {liveCount > 0 && (
              <Badge variant="default" className="bg-green-500 animate-pulse text-xs px-1.5 py-0">
                LIVE
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-muted-foreground">
            <span>{sessions.length} session{sessions.length !== 1 ? 's' : ''}</span>
            <span>{formatDuration(totalSeconds)} total</span>
            <span>Last: {formatRelativeTime(latestMs)}</span>
          </div>
        </div>

        {/* Session rows */}
        {isExpanded && (
          <div className="border-t">
            {/* Column headers */}
            <div className="flex items-center py-1 px-3 bg-muted/50 text-xs text-muted-foreground border-b">
              <div className="w-[120px] flex-shrink-0 font-medium">Igloo</div>
              <div className="flex-1 min-w-0 font-medium">Families (time)</div>
              <div className="w-[30px] flex-shrink-0 text-center font-medium" title="Number of families">Fam</div>
              <div className="w-[30px] flex-shrink-0 text-center font-medium" title="Peak concurrent">Pk</div>
              <div className="w-[45px] flex-shrink-0 text-center font-medium" title="Joins / Leaves">J/L</div>
              <div className="w-[95px] flex-shrink-0 font-medium">Start</div>
              <div className="w-[95px] flex-shrink-0 font-medium">End</div>
              <div className="w-[55px] flex-shrink-0 font-medium">Dur</div>
              <div className="w-[55px] flex-shrink-0 font-medium" title="Duration with 2+ families present">Dur+2</div>
              <div className="w-[55px] flex-shrink-0 font-medium">When</div>
              <div className="w-[55px] flex-shrink-0 font-medium" title="Time since previous session">Since</div>
              <div className="w-[30px] flex-shrink-0"></div>
            </div>
            <div className="bg-white">
              {sessions.map((session, i) => renderSessionRow(session, sinceGaps[i]))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Drill-down: show IglooSessionTimeline
  if (selectedSession) {
    return (
      <IglooSessionTimeline
        iglooId={selectedSession.iglooId}
        entryTimestamp={selectedSession.entryTimestamp}
        onBack={() => setSelectedSession(null)}
      />
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            <span>Loading sessions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const periodLabel = PERIODS.find(p => p.value === timePeriod)?.label || timePeriod;
  const totalSessionCount = liveSessions.length + historySessions.length;

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Play className="w-5 h-5" />
            Sessions
          </CardTitle>
          <div className="flex items-center gap-3">
            {/* Summary */}
            <span className="text-xs text-muted-foreground">
              {liveSessions.length > 0 && (
                <span className="text-green-600 font-medium">{liveSessions.length} live · </span>
              )}
              {historySessions.length} session{historySessions.length !== 1 ? 's' : ''} across {accountsWithSessions.length} account{accountsWithSessions.length !== 1 ? 's' : ''} in last {periodLabel}
            </span>

            {/* Time period selector */}
            <div className="flex items-center gap-1">
              {PERIODS.map(p => (
                <Button
                  key={p.value}
                  variant={timePeriod === p.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTimePeriod(p.value)}
                  className="h-7 px-2 text-xs"
                >
                  {p.label}
                </Button>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Sort:</span>
              <Select value={sortMode} onValueChange={setSortMode}>
                <SelectTrigger className="h-7 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recentSession">Most Recent</SelectItem>
                  <SelectItem value="mostSessions">Most Sessions</SelectItem>
                  <SelectItem value="alphabetic">Alphabetic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setHideLongSessions(v => !v)}
                className={`h-7 px-2 text-xs rounded-md border transition-colors ${
                  hideLongSessions
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-input hover:bg-muted'
                }`}
              >
                Hide &gt;24h
              </button>
              <button
                onClick={() => setHideSoloSessions(v => !v)}
                className={`h-7 px-2 text-xs rounded-md border transition-colors ${
                  hideSoloSessions
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-input hover:bg-muted'
                }`}
              >
                Hide solo
              </button>
            </div>

            {/* Refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={historyLoading}
              className="h-7 px-2 text-xs"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${historyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {historyLoading && (
          <div className="text-center py-2 text-xs text-muted-foreground">
            <RefreshCw className="w-4 h-4 animate-spin inline mr-1" />
            Loading sessions...
          </div>
        )}

        {totalSessionCount === 0 && !historyLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            <Snowflake className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No sessions found in the last {periodLabel}.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {accountsWithSessions.length === 0 && !historyLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                <Snowflake className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No sessions match the current filters.</p>
              </div>
            ) : (
              accountsWithSessions.map(group => renderAccountSection(group))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SessionsTab;
